/**
 * The operator's own Online switch, at the top of the rail's Settings section.
 *
 * It belongs to the person, not to this browser: Offline stops the ring in every
 * CRM window and in the phone app, which is why it sits apart from the rows below
 * it that are about this machine. The state arrives from `useCallAvailability`,
 * held by the rail, which also marks the closed heading.
 *
 * One press goes Offline with a timer, so a switch forgotten on Friday does not
 * silence somebody until they notice. The panel is for the other two lengths.
 */
import { Clock } from "lucide-react";
import { cn, Switch } from "@wa/ui";
import { useI18n } from "../../../i18n/I18nProvider";
import { DEFAULT_OFFLINE_CHOICE, OFFLINE_CHOICES, offlineUntilLabel } from "@wa/core/offline-until";
import { Pop, useClosePops } from "../../shell/Pop";
import type { CallAvailabilityApi } from "./useCallAvailability";

export function CallAvailabilitySwitch({ availability }: { availability: CallAvailabilityApi }) {
  const { t, locale } = useI18n();
  const close = useClosePops();
  const { state, busy, goOnline, goOffline } = availability;

  // Same rule as the forwarding row: a line that does not answer its calls has
  // nothing to say about who they would ring.
  if (!state?.inboundReady) return null;

  const { online, until, alone } = state;
  const label = online
    ? t("calls.online.on")
    : until
      ? t("calls.online.offUntil", { time: offlineUntilLabel(until, locale) })
      : t("calls.online.off");

  return (
    <>
      <div className="crm-msect">{t("calls.online.title")}</div>

      <div className="crm-mtag" title={t(online ? "calls.online.onHint" : "calls.online.offHint")}>
        <i style={{ background: online ? "var(--color-success)" : "var(--color-muted-foreground)" }} />
        <span className="crm-mtag-l">{label}</span>
        <Switch
          checked={online}
          disabled={busy}
          aria-label={t("calls.online.switch")}
          onCheckedChange={(next) => void (next ? goOnline() : goOffline(DEFAULT_OFFLINE_CHOICE))}
        />
      </div>

      {!online ? (
        <Pop
          matchWidth
          trigger={(open) => (
            <button type="button" className={cn("crm-mtag", open && "is-on")} aria-expanded={open}>
              <Clock className="size-3.5 shrink-0" strokeWidth={1.8} />
              <span className="crm-mtag-l">{t("calls.online.forHowLong")}</span>
            </button>
          )}
        >
          {OFFLINE_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              className="crm-mi"
              disabled={busy}
              onClick={() => {
                close();
                void goOffline(choice);
              }}
            >
              <Clock className="size-4" />
              {t(`calls.online.choice.${choice}`)}
            </button>
          ))}
        </Pop>
      ) : null}

      {/* Said out loud rather than left in a tooltip: this is the one state where
          a customer's call reaches a machine because of what was just pressed. */}
      {!online && alone ? <p className="px-2 pt-1 text-xs text-muted-foreground">{t("calls.online.alone")}</p> : null}
    </>
  );
}
